import { Modal } from '@ecomm/shared-components/lib/Modal'
import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'
import { InformationIcon } from '../../../../shared/icons/src/lib/shared-icons'

/**
 * @description Props for the InformationModal component.
 * @property title - The title of the modal.
 * @property description - The content of the modal, which can include formatted text.
 */
type InformationModalProps = {
  title: string
  description: ReactNode
}

/**
 * @description A modal component that displays information with a title and description.
 * @param {InformationModalProps} props - The props for the InformationModal component.
 * @returns A JSX element representing the modal.
 */
function InformationModal({ title, description }: InformationModalProps) {
  const [show, setShow] = useState(false)

  /**
   * @description Closes the modal.
   */
  const handleClose = useCallback(() => {
    setShow(false)
  }, [setShow])

  return (
    <>
      <button
        className="m-0 cursor-pointer border-none bg-transparent p-0"
        onClick={() => setShow(true)}
      >
        <InformationIcon className="h-6 w-6" />
      </button>
      <Modal isOpen={show} onRequestClose={handleClose}>
        <div className="prose md:prose-md lg:prose-lg prose-p:leading-tight max-w-xs bg-neutral-50 px-4 py-6 md:max-w-md md:px-10 md:py-14">
          <h2 className="mb-4 text-2xl font-bold leading-normal md:mb-6">
            {title}
          </h2>
          <p className="whitespace-pre-line">{description}</p>
          <button
            className="btn btn-solid-primary mt-4 w-64 lg:w-full"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </Modal>
    </>
  )
}

export { InformationModal }
export type { InformationModalProps }
